import React, {useState} from 'react';
import cx from 'classnames';

import {Box, Draggable, LockStyle} from '@pexip/components';

import {BreakoutParticipantRow} from '../ParticipantRow/ParticipantRow.view';
import type {
    BreakoutRoomId,
    BreakoutRoomName,
    InMeetingParticipant,
    ParticipantID,
} from '../../types';
import {BreakoutRoomVariant} from '../../types';

import styles from './BreakoutRoomParticipantList.module.scss';

export const BreakoutRoomParticipantList = React.forwardRef<
    HTMLDivElement,
    React.ComponentProps<typeof Box> & {
        participants: InMeetingParticipant[];
        myIdentity?: string;
        rooms?: Record<BreakoutRoomId, BreakoutRoomName>;
        variant?: BreakoutRoomVariant;
        scrollElementRef?: React.RefObject<HTMLElement | null>;
        onParticipantDragging?: (participantDiv: HTMLDivElement) => void;
        onParticipantDrag?: (participantDiv: HTMLDivElement) => void;
        onParticipantRoomChange?: (
            targetParticipantUuid: ParticipantID,
            targetRoomId: BreakoutRoomId,
        ) => void;
        lockStyle?: LockStyle;
    }
>(
    (
        {
            participants,
            myIdentity,
            rooms,
            variant = BreakoutRoomVariant.Setup,
            scrollElementRef,
            onParticipantDragging,
            onParticipantDrag,
            onParticipantRoomChange,
            className,
            lockStyle = LockStyle.VERTICAL,
            ...props
        },
        ref,
    ) => {
        const [draggedParticipant, setDraggedParticipant] = useState<
            string | undefined
        >();
        return (
            <Box {...props} ref={ref} className={cx(styles.wrapper, className)}>
                {participants.map(participant => (
                    <Draggable
                        scrollElementRef={scrollElementRef}
                        pointerCapture={true}
                        className={cx(styles.item, {
                            [styles.dragging]:
                                variant !== BreakoutRoomVariant.Preview &&
                                draggedParticipant === participant.uuid,
                        })}
                        id={participant.uuid}
                        key={participant.uuid}
                        onPointerMoveExtra={onParticipantDragging}
                        onPointerUpExtra={onParticipantDrag}
                        onPointerDown={() =>
                            setDraggedParticipant(participant.uuid)
                        }
                        onPointerUp={() => setDraggedParticipant(undefined)}
                        isDisabled={
                            variant === BreakoutRoomVariant.Preview ||
                            !rooms ||
                            (rooms && Object.entries(rooms).length === 0)
                        }
                        lockStyle={lockStyle}
                    >
                        <BreakoutParticipantRow
                            participant={participant}
                            isCurrentPeer={participant.uuid === myIdentity}
                            rooms={rooms}
                            isDragging={draggedParticipant !== undefined}
                            onParticipantRoomChange={onParticipantRoomChange}
                            className={cx(styles.draggableContent, {
                                [styles.dragging]:
                                    variant !== BreakoutRoomVariant.Preview &&
                                    draggedParticipant === participant.uuid,
                            })}
                        />
                    </Draggable>
                ))}
            </Box>
        );
    },
);

BreakoutRoomParticipantList.displayName = 'BreakoutRoomParticipantList';
